import { toast } from '@shared/components/ui/Toaster';
import { retryOperation } from './helpers';
import {
  getMaxFileSize,
  getSupportedFormats,
  getDescriptionLimits,
  getFormattedMaxFileSize,
  getSupportedFormatsString,
  isSupportedFormat,
  isDebugMode
} from '../config/jobMatchConfig';

export enum JobMatchErrorType {
  FILE_VALIDATION = 'FILE_VALIDATION',
  FILE_SIZE = 'FILE_SIZE',
  FILE_READ = 'FILE_READ',
  NETWORK = 'NETWORK',
  DATABASE = 'DATABASE',
  AI_PROCESSING = 'AI_PROCESSING',
  AUTHENTICATION = 'AUTHENTICATION',
  VALIDATION = 'VALIDATION',
  RATE_LIMIT = 'RATE_LIMIT',
  TIMEOUT = 'TIMEOUT',
  UNKNOWN = 'UNKNOWN'
}

export interface JobMatchError {
  type: JobMatchErrorType;
  message: string;
  userMessage: string;
  canRetry: boolean;
  retryDelay?: number;
  details?: any;
}

export const validateJobDescriptionFile = (file: File): JobMatchError | null => {
  const maxFileSize = getMaxFileSize();

  if (file.size > maxFileSize) {
    return {
      type: JobMatchErrorType.FILE_SIZE,
      message: `File size ${file.size} exceeds maximum ${maxFileSize}`,
      userMessage: `File is too large. Please upload a file smaller than ${getFormattedMaxFileSize()}.`,
      canRetry: false
    };
  }

  const fileExtension = file.name.split('.').pop()?.toLowerCase() || '';

  if (!isSupportedFormat(fileExtension)) {
    return {
      type: JobMatchErrorType.FILE_VALIDATION,
      message: `Invalid file type: ${file.type} (${fileExtension})`,
      userMessage: `Please upload a supported file format: ${getSupportedFormatsString()}.`,
      canRetry: false,
      details: { fileType: file.type, fileName: file.name, extension: fileExtension }
    };
  }

  return null;
};

export const validateJobDescription = (description: string): JobMatchError | null => {
  const trimmed = description.trim();
  const { min, max } = getDescriptionLimits();

  if (!trimmed) {
    return {
      type: JobMatchErrorType.VALIDATION,
      message: 'Job description is empty',
      userMessage: 'Please enter or upload a job description.',
      canRetry: false
    };
  }

  if (trimmed.length < min) {
    return {
      type: JobMatchErrorType.VALIDATION,
      message: `Job description too short: ${trimmed.length} characters`,
      userMessage: `Job description is too short (${trimmed.length} characters). Please provide at least ${min} characters with more details about the role.`,
      canRetry: false
    };
  }

  if (trimmed.length > max) {
    return {
      type: JobMatchErrorType.VALIDATION,
      message: `Job description too long: ${trimmed.length} characters`,
      userMessage: `Job description is too long (${trimmed.length} characters). Please limit it to ${max.toLocaleString()} characters.`,
      canRetry: false
    };
  }

  return null;
};

export const validateFormInputs = (jobTitle: string, companyName: string): JobMatchError | null => {
  if (!jobTitle.trim()) {
    return {
      type: JobMatchErrorType.VALIDATION,
      message: 'Job title is empty',
      userMessage: 'Please enter a job title.',
      canRetry: false
    };
  }

  if (jobTitle.trim().length < 3) {
    return {
      type: JobMatchErrorType.VALIDATION,
      message: 'Job title too short',
      userMessage: 'Job title must be at least 3 characters long.',
      canRetry: false
    };
  }

  if (!companyName.trim()) {
    return {
      type: JobMatchErrorType.VALIDATION,
      message: 'Company name is empty',
      userMessage: 'Please enter a company name.',
      canRetry: false
    };
  }

  if (companyName.trim().length < 2) {
    return {
      type: JobMatchErrorType.VALIDATION,
      message: 'Company name too short',
      userMessage: 'Company name must be at least 2 characters long.',
      canRetry: false
    };
  }

  return null;
};

export const classifyError = (error: any): JobMatchError => {
  const errorMessage = error?.message || String(error);
  const errorCode = error?.code || error?.status;

  if (errorMessage.includes('Failed to fetch') || errorMessage.includes('NetworkError')) {
    return {
      type: JobMatchErrorType.NETWORK,
      message: errorMessage,
      userMessage: 'Network connection lost. Please check your internet connection and try again.',
      canRetry: true,
      retryDelay: 2000
    };
  }

  if (errorCode === 'PGRST116' || errorMessage.includes('not found')) {
    return {
      type: JobMatchErrorType.DATABASE,
      message: errorMessage,
      userMessage: 'The requested data was not found. Please try again.',
      canRetry: false
    };
  }

  if (errorCode === '23505' || errorMessage.includes('duplicate')) {
    return {
      type: JobMatchErrorType.DATABASE,
      message: errorMessage,
      userMessage: 'This job match already exists. Please try a different job description.',
      canRetry: false
    };
  }

  if (errorCode === '42501' || errorMessage.includes('permission') || errorMessage.includes('policy')) {
    return {
      type: JobMatchErrorType.AUTHENTICATION,
      message: errorMessage,
      userMessage: 'You do not have permission to perform this action. Please log in and try again.',
      canRetry: false
    };
  }

  if (errorMessage.includes('timeout') || errorMessage.includes('ETIMEDOUT')) {
    return {
      type: JobMatchErrorType.TIMEOUT,
      message: errorMessage,
      userMessage: 'The request took too long to complete. Please try again.',
      canRetry: true,
      retryDelay: 3000
    };
  }

  if (errorMessage.includes('rate limit') || errorCode === 429) {
    return {
      type: JobMatchErrorType.RATE_LIMIT,
      message: errorMessage,
      userMessage: 'Too many requests. Please wait a moment and try again.',
      canRetry: true,
      retryDelay: 5000
    };
  }

  if (errorMessage.includes('AI') || errorMessage.includes('OpenAI') || errorMessage.includes('model')) {
    return {
      type: JobMatchErrorType.AI_PROCESSING,
      message: errorMessage,
      userMessage: 'AI analysis is temporarily unavailable. Your request will be retried automatically.',
      canRetry: true,
      retryDelay: 3000
    };
  }

  if (errorMessage.includes('database') || errorMessage.includes('connection')) {
    return {
      type: JobMatchErrorType.DATABASE,
      message: errorMessage,
      userMessage: 'Database connection issue. Please try again in a moment.',
      canRetry: true,
      retryDelay: 2000
    };
  }

  return {
    type: JobMatchErrorType.UNKNOWN,
    message: errorMessage,
    userMessage: 'An unexpected error occurred. Please try again or contact support if the issue persists.',
    canRetry: true,
    retryDelay: 2000,
    details: error
  };
};

export const handleJobMatchError = (error: any, showToast: boolean = true): JobMatchError => {
  const classifiedError = classifyError(error);

  if (isDebugMode()) {
    console.error('[Job Match Error - Debug]', {
      type: classifiedError.type,
      message: classifiedError.message,
      canRetry: classifiedError.canRetry,
      details: classifiedError.details,
      originalError: error
    });
  } else {
    console.error('[Job Match Error]', {
      type: classifiedError.type,
      message: classifiedError.message,
      canRetry: classifiedError.canRetry
    });
  }

  if (showToast) {
    toast.error(classifiedError.userMessage);
  }

  return classifiedError;
};

export const retryWithErrorHandling = async <T>(
  operation: () => Promise<T>,
  operationName: string,
  maxRetries: number = 3
): Promise<T> => {
  try {
    return await retryOperation(operation, maxRetries);
  } catch (error) {
    const jobMatchError = handleJobMatchError(error);

    if (jobMatchError.canRetry) {
      toast.error(`${jobMatchError.userMessage} (Retried ${maxRetries} times)`);
    }

    throw jobMatchError;
  }
};

export interface PersonaResult<T> {
  success: boolean;
  data?: T;
  error?: JobMatchError;
  personaName: string;
}

export const executePersonaWithFallback = async <T>(
  personaName: string,
  operation: () => Promise<T>,
  fallbackData?: Partial<T>
): Promise<PersonaResult<T>> => {
  try {
    const data = await operation();
    return {
      success: true,
      data,
      personaName
    };
  } catch (error) {
    const jobMatchError = handleJobMatchError(error, false);

    console.warn(`[Persona Failed] ${personaName}:`, jobMatchError.message);

    return {
      success: false,
      error: jobMatchError,
      personaName,
      data: fallbackData as T
    };
  }
};

export const aggregatePersonaResults = <T>(
  results: PersonaResult<T>[]
): {
  successful: PersonaResult<T>[];
  failed: PersonaResult<T>[];
  hasPartialSuccess: boolean;
  hasCompleteFailure: boolean;
} => {
  const successful = results.filter(r => r.success);
  const failed = results.filter(r => !r.success);

  return {
    successful,
    failed,
    hasPartialSuccess: successful.length > 0 && failed.length > 0,
    hasCompleteFailure: successful.length === 0
  };
};

export const getUserFriendlyErrorMessage = (error: JobMatchError): string => {
  const baseMessage = error.userMessage;

  if (error.canRetry && error.retryDelay) {
    return `${baseMessage} We'll automatically retry in ${error.retryDelay / 1000} seconds.`;
  }

  return baseMessage;
};

export const logErrorToMonitoring = (error: JobMatchError, context?: any): void => {
  const logData = {
    timestamp: new Date().toISOString(),
    errorType: error.type,
    message: error.message,
    userMessage: error.userMessage,
    canRetry: error.canRetry,
    ...(isDebugMode() && { context, details: error.details })
  };

  if (isDebugMode()) {
    console.error('[Job Match Error Logged - Debug]', logData);
  } else {
    console.error('[Job Match Error Logged]', {
      timestamp: logData.timestamp,
      errorType: logData.errorType,
      canRetry: logData.canRetry
    });
  }
};
